import React, { useEffect, useState, useCallback } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  InfoCard,
  StatusOK,
  StatusError,
  StatusPending,
  StatusRunning,
} from '@backstage/core-components';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Typography,
  Link,
  Chip,
  IconButton,
  Divider,
  Tooltip,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useApi, configApiRef, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import RefreshIcon from '@material-ui/icons/Refresh';
import TuneIcon from '@material-ui/icons/Tune';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import MapIcon from '@material-ui/icons/AccountTree';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';

const ORG_ID_ANNOTATION     = 'humanitec.dev/orgId';
const PROJECT_ID_ANNOTATION = 'humanitec.dev/projectId';
const MODULE_ANNOTATION     = 'humanitec.dev/moduleId';
const GITEA_SLUG_ANNOTATION = 'gitea.kyndemo.live/repo-slug';

// Also accept the legacy humanitec.com/* annotation prefix used by older skeletons
const getAnnotation = (entity: any, devKey: string, comKey: string): string | undefined =>
  entity?.metadata?.annotations?.[devKey] ?? entity?.metadata?.annotations?.[comKey];

export const isHumanitecV2Available = (entity: any) =>
  Boolean(
    getAnnotation(entity, 'humanitec.dev/orgId',     'humanitec.com/orgId') &&
    getAnnotation(entity, 'humanitec.dev/projectId', 'humanitec.com/projectId')
  );

type Environment = {
  id: string;
  display_name: string;
  env_type_id: string;
  runner_id: string;
  status: string;
  updated_at: string;
};

type Deployment = {
  id: string;
  status: string;
  mode: string;
  created_at: string;
  completed_at?: string;
  metrics: {
    num_workloads: number;
    num_resource_nodes: number;
  };
};

type ResourceNode = {
  resource_id: string;
  resource_type: string;
};

type ActiveResource = {
  res_id: string;
  type: string;
  // status field names vary across Humanitec API versions
  status?: string;
  deploy_status?: string;
  resource_status?: string;
  state?: string;
  // other known fields
  gu_res_id?: string;
  driver_type?: string;
  driver_account?: string;
  resource_id?: string;
  resource_type?: string;
  // catch-all for unknown fields we want to inspect
  [key: string]: unknown;
};

type ActionResult = { ok: true; message: string; prUrl?: string } | { ok: false; message: string };

type PastDeployment = { id: string; status: string; created_at: string };

const useStyles = makeStyles(theme => ({
  chip: {
    marginLeft: theme.spacing(0.5),
  },
  consoleLink: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  actionCell: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  resultText: {
    fontSize: '0.78rem',
    marginTop: theme.spacing(0.5),
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '0.78rem',
    marginTop: theme.spacing(0.5),
  },
  sliderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  slider: { flex: 1 },
  replicaBadge: { minWidth: 28, textAlign: 'center', fontWeight: 700 },
}));

// ── SVG resource map diagram ─────────────────────────────────────────────────
// One group per workload. Each group has a hub node at the top with resource
// nodes spread **horizontally** below it, edges fanning out like a tree.
// Groups themselves are laid out horizontally (left → right).
// The entity's own workload is highlighted; others are muted.
function ResourceMapDiagram({
  workloadId,
  resources,
  onResourceClick,
}: {
  workloadId: string;
  resources: ActiveResource[];
  onResourceClick: (r: ActiveResource) => void;
}) {
  const theme = useTheme();

  const PAD       = 24;
  const WL_W      = 156;
  const WL_H      = 56;
  const RES_W     = 136;
  const RES_H     = 58;
  const RES_H_GAP = 12;  // horizontal gap between sibling resource nodes
  const V_CONN    = 52;  // vertical gap: workload bottom → resource tops
  const GRP_GAP   = 36;  // horizontal gap between workload groups

  // ------------------------------------------------------------------
  // 1. Group resources by owning workload
  // ------------------------------------------------------------------
  const groupMap = new Map<string, ActiveResource[]>();

  resources.forEach(r => {
    const id = r.res_id ?? r.resource_id ?? '';
    if (id.startsWith('modules.')) {
      const wl = id.slice('modules.'.length);
      if (!groupMap.has(wl)) groupMap.set(wl, []);
    }
  });

  resources.forEach(r => {
    const id = r.res_id ?? r.resource_id ?? '';
    if (id.startsWith('modules.')) return;
    const m = id.match(/^workloads\.([^.]+)\./);
    if (m) {
      if (!groupMap.has(m[1])) groupMap.set(m[1], []);
      groupMap.get(m[1])!.push(r);
    }
  });

  if (groupMap.size === 0) {
    return <Typography color="textSecondary">No workload resources found for this environment.</Typography>;
  }

  // ------------------------------------------------------------------
  // 2. Sort: current workload first, then alphabetical
  // ------------------------------------------------------------------
  const sortedIds = Array.from(groupMap.keys()).sort((a, b) => {
    if (a === workloadId) return -1;
    if (b === workloadId) return 1;
    return a.localeCompare(b);
  });

  // ------------------------------------------------------------------
  // 3. Compute group widths and X offsets
  //    Each group width = max(WL_W, nRes * RES_W + (nRes-1) * RES_H_GAP)
  // ------------------------------------------------------------------
  type LayoutGroup = {
    wlId:    string;
    res:     ActiveResource[];
    groupX:  number;  // left edge of this group
    groupW:  number;  // total width of this group
  };

  const groups: LayoutGroup[] = [];
  let curX = PAD;

  sortedIds.forEach(wlId => {
    const res = groupMap.get(wlId) ?? [];
    const resBand = res.length > 0
      ? res.length * RES_W + (res.length - 1) * RES_H_GAP
      : WL_W;
    const groupW = Math.max(WL_W, resBand);
    groups.push({ wlId, res, groupX: curX, groupW });
    curX += groupW + GRP_GAP;
  });

  const svgW = curX - GRP_GAP + PAD;
  const svgH = PAD + WL_H + V_CONN + RES_H + PAD;

  // ------------------------------------------------------------------
  // 4. Theme colours
  // ------------------------------------------------------------------
  const primary   = theme.palette.primary.main;
  const divider   = theme.palette.divider;
  const txtMain   = theme.palette.text.primary;
  const txtSub    = theme.palette.text.secondary;
  const bgDefault = theme.palette.background.default;
  const bgPaper   = theme.palette.background.paper;

  const trunc = (s: string, n: number) =>
    s.length > n ? `${s.slice(0, n - 1)}\u2026` : s;

  const resolveStatus = (r: ActiveResource): string =>
    (r.status || r.deploy_status || r.resource_status || r.state || '') as string || 'active';

  const statusDot = (s: string) => {
    if (s === 'active')  return '#4caf50';
    if (s === 'pending') return '#ff9800';
    if (s === 'failed')  return '#f44336';
    return '#9e9e9e';
  };

  return (
    <Box style={{ overflowX: 'auto', padding: '8px 0' }}>
      <svg width={svgW} height={svgH} style={{ display: 'block' }}>
        <defs>
          <marker id="arr-cur" markerWidth="7" markerHeight="7" refX="3" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6 z" fill={primary} opacity="0.8" />
          </marker>
          <marker id="arr-oth" markerWidth="7" markerHeight="7" refX="3" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6 z" fill={divider} opacity="0.7" />
          </marker>
        </defs>

        {groups.map(({ wlId, res, groupX, groupW }) => {
          const isCurrent    = wlId === workloadId;
          const groupOpacity = isCurrent ? 1 : 0.45;

          // Workload hub — centred within group
          const wlX  = groupX + (groupW - WL_W) / 2;
          const wlY  = PAD;
          const wlCX = groupX + groupW / 2;  // true centre of group
          const wlBY = wlY + WL_H;

          // Resources — spread horizontally, centred within group
          const resBand = res.length > 0
            ? res.length * RES_W + (res.length - 1) * RES_H_GAP
            : 0;
          const resLeft = groupX + (groupW - resBand) / 2;
          const resY    = wlBY + V_CONN;

          const wlStroke  = isCurrent ? primary : divider;
          const wlFill    = isCurrent ? `${primary}1E` : bgDefault;
          const wlTxtCol  = isCurrent ? primary : txtSub;
          const edgeColor = isCurrent ? primary : divider;
          const marker    = isCurrent ? 'url(#arr-cur)' : 'url(#arr-oth)';

          return (
            <g key={wlId} opacity={groupOpacity}>
              {/* ── Fan-out bezier edges: hub → each resource ── */}
              {res.map((r, i) => {
                const rx   = resLeft + i * (RES_W + RES_H_GAP);
                const rCX  = rx + RES_W / 2;
                const cpY  = wlBY + V_CONN / 2;
                return (
                  <path
                    key={`edge-${r.res_id ?? i}`}
                    d={`M ${wlCX} ${wlBY} C ${wlCX} ${cpY} ${rCX} ${cpY} ${rCX} ${resY}`}
                    fill="none"
                    stroke={edgeColor}
                    strokeWidth={1.5}
                    markerEnd={marker}
                  />
                );
              })}

              {/* ── Workload hub node ── */}
              <rect
                x={wlX} y={wlY} width={WL_W} height={WL_H} rx={8}
                fill={wlFill} stroke={wlStroke} strokeWidth={isCurrent ? 2 : 1}
              />
              <text
                x={wlCX} y={wlY + 23}
                textAnchor="middle"
                fill={wlTxtCol} fontSize={12}
                fontWeight={isCurrent ? '700' : '400'}
                fontFamily="inherit"
              >
                {trunc(wlId, 20)}
              </text>
              <text
                x={wlCX} y={wlY + 40}
                textAnchor="middle"
                fill={txtSub} fontSize={9} fontFamily="inherit"
              >
                {isCurrent ? 'workload \u2022 current' : 'workload'}
              </text>

              {/* ── Resource nodes ── */}
              {res.map((r, i) => {
                const rx    = resLeft + i * (RES_W + RES_H_GAP);
                const rCX   = rx + RES_W / 2;
                const rawId = r.res_id ?? r.resource_id ?? '';
                const rType = r.type ?? r.resource_type ?? 'unknown';
                const rStat = resolveStatus(r);
                const label = rawId.split('.').pop() ?? rawId;
                const dot   = statusDot(rStat);

                return (
                  <g
                    key={`res-${rawId}-${i}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => onResourceClick(r)}
                  >
                    {/* Invisible hit-area slightly larger than visible card */}
                    <rect
                      x={rx - 2} y={resY - 2} width={RES_W + 4} height={RES_H + 4} rx={8}
                      fill="transparent"
                    />
                    <rect
                      x={rx} y={resY} width={RES_W} height={RES_H} rx={6}
                      fill={bgDefault} stroke={isCurrent ? `${primary}66` : divider} strokeWidth={1}
                    />
                    {/* Type badge strip */}
                    <rect
                      x={rx + 1} y={resY + 1} width={RES_W - 2} height={18} rx={5}
                      fill={bgPaper} opacity={0.5}
                    />
                    <text
                      x={rCX} y={resY + 14}
                      textAnchor="middle"
                      fill={txtSub} fontSize={9} fontFamily="inherit"
                    >
                      {trunc(rType, 20)}
                    </text>
                    <text
                      x={rCX} y={resY + 36}
                      textAnchor="middle"
                      fill={isCurrent ? txtMain : txtSub}
                      fontSize={12}
                      fontWeight={isCurrent ? '600' : '400'}
                      fontFamily="inherit"
                    >
                      {trunc(label, 16)}
                    </text>
                    <circle cx={rx + 9} cy={resY + RES_H - 9} r={3} fill={dot} />
                    <text
                      x={rx + 16} y={resY + RES_H - 5}
                      fill={txtSub} fontSize={9} fontFamily="inherit"
                    >
                      {trunc(rStat, 14)}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
    </Box>
  );
}

function DeployStatus({ status }: { status: string }) {
  switch (status) {
    case 'succeeded':   return <StatusOK>{status}</StatusOK>;
    case 'failed':      return <StatusError>{status}</StatusError>;
    case 'in progress': return <StatusRunning>{status}</StatusRunning>;
    default:            return <StatusPending>{status}</StatusPending>;
  }
}

export const HumanitecV2Content = () => {
  const { entity } = useEntity();
  const config = useApi(configApiRef);
  const discovery = useApi(discoveryApiRef);
  const { fetch: authedFetch } = useApi(fetchApiRef);
  const classes = useStyles();
  const theme = useTheme();

  const annotations = entity.metadata.annotations ?? {};
  const orgId      = getAnnotation(entity, ORG_ID_ANNOTATION,     'humanitec.com/orgId');
  const projectId  = getAnnotation(entity, PROJECT_ID_ANNOTATION, 'humanitec.com/projectId');
  const workloadId = entity.metadata.name;
  const moduleName = (getAnnotation(entity, MODULE_ANNOTATION, 'humanitec.com/moduleId') ?? annotations[MODULE_ANNOTATION]) || workloadId;
  const giteaSlug  = annotations[GITEA_SLUG_ANNOTATION] ?? '';
  const [giteaOwner, giteaRepo] = giteaSlug.split('/');

  const backendUrl  = config.getString('backend.baseUrl');
  const proxyBase   = `${backendUrl}/api/proxy/humanitec-v2`;
  const consoleBase = `https://console.humanitec.dev/${orgId}/projects/${projectId}`;

  // ── Orchestrator state ───────────────────────────────────────────────────
  const [envs, setEnvs] = useState<Environment[]>([]);
  const [deployments, setDeployments] = useState<Record<string, Deployment | null>>({});
  const [workloadPresent, setWorkloadPresent] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  // ── Scale action state ───────────────────────────────────────────────────
  const [scaleEnv, setScaleEnv] = useState<Environment | null>(null);
  const [scaleValue, setScaleValue] = useState(2);
  const [scaleLoading, setScaleLoading] = useState(false);
  const [scaleResult, setScaleResult] = useState<ActionResult | null>(null);

  // ── Rollback action state ────────────────────────────────────────────────
  const [rollbackEnv, setRollbackEnv] = useState<Environment | null>(null);
  const [pastDeploys, setPastDeploys] = useState<PastDeployment[]>([]);
  const [pastDeploysLoading, setPastDeploysLoading] = useState(false);
  const [selectedDeploy, setSelectedDeploy] = useState('');
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [rollbackResult, setRollbackResult] = useState<ActionResult | null>(null);

  // ── Promote action state ─────────────────────────────────────────────────
  const [promoteLoading, setPromoteLoading] = useState<string | null>(null);
  const [promoteResult, setPromoteResult] = useState<ActionResult | null>(null);

  // ── Environment Map state (per-env inline) ───────────────────────────────
  const [mapOpen, setMapOpen] = useState<Record<string, boolean>>({});
  const [mapData, setMapData] = useState<Record<string, ActiveResource[]>>({});
  const [mapFetching, setMapFetching] = useState<Record<string, boolean>>({});

  const toggleMap = useCallback(async (env: Environment) => {
    if (mapData[env.id] !== undefined) {
      setMapOpen(prev => ({ ...prev, [env.id]: !prev[env.id] }));
      return;
    }
    setMapOpen(prev => ({ ...prev, [env.id]: true }));
    setMapFetching(prev => ({ ...prev, [env.id]: true }));
    try {
      const res = await fetch(
        `${proxyBase}/orgs/${orgId}/active-resources?env_id=${env.id}&project_id=${projectId}`,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const items: ActiveResource[] = data.items ?? [];
      // Log first item so we can inspect the real API shape in devtools
      if (items.length > 0) {
        // eslint-disable-next-line no-console
        console.debug('[HumanitecV2] active-resource sample:', JSON.stringify(items[0], null, 2));
      }
      setMapData(prev => ({ ...prev, [env.id]: items }));
    } catch {
      setMapData(prev => ({ ...prev, [env.id]: [] }));
    } finally {
      setMapFetching(prev => ({ ...prev, [env.id]: false }));
    }
  }, [mapData, proxyBase, orgId, projectId]);

  // ── Resource detail state ─────────────────────────────────────────────────
  const [detailResource, setDetailResource] = useState<ActiveResource | null>(null);

  const getBaseUrl = useCallback(() => discovery.getBaseUrl('app'), [discovery]);

  // ── Load orchestrator data ───────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!orgId || !projectId) return;
    setLoading(true);
    setError(undefined);
    try {
      const envsRes = await fetch(`${proxyBase}/orgs/${orgId}/projects/${projectId}/envs`);
      if (!envsRes.ok) throw new Error(`Environments fetch failed: ${envsRes.status}`);
      const envsData: Environment[] = (await envsRes.json()).items ?? [];

      // Sort envs: dev → staging → prod, then any others alphabetically
      const ENV_ORDER: Record<string, number> = { dev: 0, development: 0, staging: 1, stage: 1, prod: 2, production: 2 };
      const sortKey = (e: Environment) => {
        const id = e.id.toLowerCase();
        if (id in ENV_ORDER) return ENV_ORDER[id];
        const typeId = (e.env_type_id ?? '').toLowerCase();
        if (typeId in ENV_ORDER) return ENV_ORDER[typeId];
        return 10 + e.id.localeCompare('z');
      };
      setEnvs([...envsData].sort((a, b) => sortKey(a) - sortKey(b)));

      const deployMap: Record<string, Deployment | null> = {};
      const workloadMap: Record<string, boolean> = {};
      await Promise.all(
        envsData.map(async env => {
          const [deployRes, nodesRes] = await Promise.all([
            fetch(`${proxyBase}/orgs/${orgId}/deployments?env_id=${env.id}&project_id=${projectId}`),
            fetch(`${proxyBase}/orgs/${orgId}/active-resources?env_id=${env.id}&project_id=${projectId}`),
          ]);
          if (deployRes.ok) {
            const deploys: Deployment[] = (await deployRes.json()).items ?? [];
            deployMap[env.id] = deploys[0] ?? null;
          } else {
            deployMap[env.id] = null;
          }
          if (nodesRes.ok) {
            const nodes: ResourceNode[] = (await nodesRes.json()).items ?? [];
            workloadMap[env.id] = nodes.some(
              n => n.resource_id === workloadId || n.resource_id.startsWith(`workloads.${workloadId}.`),
            );
          } else {
            workloadMap[env.id] = false;
          }
        })
      );
      setDeployments(deployMap);
      setWorkloadPresent(workloadMap);
    } catch (e: any) {
      setError(e.message ?? String(e));
    } finally {
      setLoading(false);
    }
  }, [orgId, projectId, workloadId, proxyBase]);

  useEffect(() => { load(); }, [load]);

  // ── Scale handler ────────────────────────────────────────────────────────
  const handleScale = async () => {
    if (!scaleEnv) return;
    setScaleLoading(true);
    setScaleResult(null);
    try {
      const baseUrl = await getBaseUrl();
      const resp = await authedFetch(`${baseUrl}/api/day2-actions/scale-humanitec`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appId: projectId, envId: scaleEnv.id, moduleName, replicas: scaleValue }),
      });
      const data = await resp.json();
      setScaleResult(resp.ok ? { ok: true, message: data.message } : { ok: false, message: data.error || `HTTP ${resp.status}` });
    } catch (err: any) {
      setScaleResult({ ok: false, message: err.message || 'Request failed' });
    } finally {
      setScaleLoading(false);
    }
  };

  // ── Rollback helpers ─────────────────────────────────────────────────────
  const openRollback = async (env: Environment) => {
    setRollbackEnv(env);
    setSelectedDeploy('');
    setRollbackResult(null);
    setPastDeploys([]);
    setPastDeploysLoading(true);
    try {
      const baseUrl = await getBaseUrl();
      const resp = await authedFetch(
        `${baseUrl}/api/day2-actions/deployments?appId=${encodeURIComponent(projectId!)}&envId=${encodeURIComponent(env.id)}`,
      );
      const data = await resp.json();
      setPastDeploys(Array.isArray(data) ? data : []);
    } catch {
      setPastDeploys([]);
    } finally {
      setPastDeploysLoading(false);
    }
  };

  const handleRollback = async () => {
    if (!rollbackEnv || !selectedDeploy) return;
    setRollbackLoading(true);
    setRollbackResult(null);
    try {
      const baseUrl = await getBaseUrl();
      const resp = await authedFetch(`${baseUrl}/api/day2-actions/rollback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appId: projectId, envId: rollbackEnv.id, deployId: selectedDeploy }),
      });
      const data = await resp.json();
      if (resp.ok) {
        setRollbackResult({ ok: true, message: `Rollback to ${selectedDeploy} triggered.` });
        setRollbackEnv(null);
      } else {
        setRollbackResult({ ok: false, message: data.error || `HTTP ${resp.status}` });
      }
    } catch (err: any) {
      setRollbackResult({ ok: false, message: err.message || 'Request failed' });
    } finally {
      setRollbackLoading(false);
    }
  };

  // ── Promote handler — opens a Gitea PR to gate the promotion via GitOps ──
  const handlePromote = async (sourceEnv: Environment, targetEnv: Environment) => {
    const key = `${sourceEnv.id}->${targetEnv.id}`;
    setPromoteLoading(key);
    setPromoteResult(null);
    try {
      const promoteBase = await discovery.getBaseUrl('gitea-promote');
      const resp = await authedFetch(`${promoteBase}/open-pr`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          repoOwner: giteaOwner,
          repoName:  giteaRepo,
          head:  sourceEnv.id,
          base:  targetEnv.id,
          title: `chore(platform): promote ${sourceEnv.id} → ${targetEnv.id}`,
          body: [
            `## Platform Promotion: \`${giteaRepo}\``,
            '',
            `Promoting \`${sourceEnv.id}\` → \`${targetEnv.id}\` initiated from Backstage (Humanitec tab).`,
            '',
            'Integration tests must pass and 1 approval is required before merge.',
          ].join('\n'),
        }),
      });
      const data = await resp.json();
      if (resp.ok) {
        setPromoteResult({ ok: true, message: `PR #${data.prNumber} opened — awaiting CI gate + approval.`, prUrl: data.prUrl });
      } else {
        setPromoteResult({ ok: false, message: data.error || `HTTP ${resp.status}` });
      }
    } catch (err: any) {
      setPromoteResult({ ok: false, message: err.message || 'Request failed' });
    } finally {
      setPromoteLoading(null);
    }
  };

  if (!orgId || !projectId) {
    return (
      <InfoCard title="Humanitec Platform Orchestrator v2">
        <Typography color="error">
          Missing annotations: <code>humanitec.dev/orgId</code> and{' '}
          <code>humanitec.dev/projectId</code> are required.
        </Typography>
      </InfoCard>
    );
  }

  return (
    <>
      <InfoCard
        title="Humanitec Platform Orchestrator v2"
        subheader={
          <>
            Org: <strong>{orgId}</strong> · Project:{' '}
            <Link href={consoleBase} target="_blank" rel="noopener noreferrer">
              {projectId}
            </Link>
          </>
        }
        action={
          <IconButton onClick={load} disabled={loading} aria-label="refresh">
            <RefreshIcon />
          </IconButton>
        }
      >
        {loading && (
          <Box>
            {[1, 2, 3].map(n => (
              <Box key={n} style={{ height: 48, marginBottom: 8, borderRadius: 4, backgroundColor: theme.palette.action.hover }} />
            ))}
          </Box>
        )}
        {!loading && error && (
          <Typography color="error">Failed to load data: {error}</Typography>
        )}
        {!loading && !error && envs.length === 0 && (
          <Typography color="textSecondary">No environments found for this project.</Typography>
        )}
        {!loading && !error && envs.length > 0 && (
          <Box>
            {/* Header row */}
            <Box
              display="flex"
              style={{
                padding: '6px 16px',
                borderBottom: `1px solid ${theme.palette.divider}`,
                opacity: 0.6,
              }}
            >
              <Box style={{ flex: 2 }}><Typography variant="caption">ENVIRONMENT</Typography></Box>
              <Box style={{ flex: 1.5 }}><Typography variant="caption">RUNNER</Typography></Box>
              <Box style={{ flex: 1.5 }}><Typography variant="caption">WORKLOAD</Typography></Box>
              <Box style={{ flex: 2 }}><Typography variant="caption">LAST DEPLOYMENT</Typography></Box>
              <Box style={{ flex: 2 }}><Typography variant="caption">ACTIONS</Typography></Box>
            </Box>
            {envs.map((env, idx) => {
              const d = deployments[env.id];
              const ts = d ? (d.completed_at ?? d.created_at) : null;
              const isMapOpen = Boolean(mapOpen[env.id]);
              const nextEnv = envs[idx + 1];
              // Only show promote between non-prod environments (never promote FROM prod)
              const isProd = ['prod', 'production'].includes(env.id.toLowerCase()) ||
                             ['prod', 'production'].includes((env.env_type_id ?? '').toLowerCase());
              const showPromote = !isProd && Boolean(nextEnv);
              return (
                <React.Fragment key={env.id}>
                  <Box style={{ borderBottom: showPromote ? 'none' : `1px solid ${theme.palette.divider}` }}>
                  {/* Data row */}
                  <Box display="flex" alignItems="center" style={{ padding: '10px 16px' }}>
                    <Box style={{ flex: 2 }}>
                      <Link
                        href={`${consoleBase}/environments/${env.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontWeight: 700 }}
                      >
                        {env.display_name}
                      </Link>
                      <Chip size="small" label={env.env_type_id} className={classes.chip} variant="outlined" />
                    </Box>
                    <Box style={{ flex: 1.5 }}>
                      <Typography variant="body2">{env.runner_id || '—'}</Typography>
                    </Box>
                    <Box style={{ flex: 1.5 }}>
                      {workloadPresent[env.id]
                        ? <StatusOK>deployed</StatusOK>
                        : <StatusError>not deployed</StatusError>}
                    </Box>
                    <Box style={{ flex: 2 }}>
                      {d ? (
                        <Box>
                          <DeployStatus status={d.status} />
                          <Typography variant="caption" display="block" color="textSecondary">
                            {new Date(ts!).toLocaleString()}
                          </Typography>
                          <Typography variant="caption" display="block" color="textSecondary">
                            {d.metrics.num_workloads} workload{d.metrics.num_workloads !== 1 ? 's' : ''} ·{' '}
                            {d.metrics.num_resource_nodes} resources
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">—</Typography>
                      )}
                    </Box>
                    <Box style={{ flex: 2 }} className={classes.actionCell}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<TuneIcon fontSize="small" />}
                        onClick={() => { setScaleResult(null); setScaleValue(2); setScaleEnv(env); }}
                      >Scale</Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        startIcon={<RotateLeftIcon fontSize="small" />}
                        onClick={() => openRollback(env)}
                      >Rollback</Button>
                      <Button
                        size="small"
                        variant={isMapOpen ? 'contained' : 'outlined'}
                        startIcon={<MapIcon fontSize="small" />}
                        endIcon={isMapOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        onClick={() => toggleMap(env)}
                      >Map</Button>
                    </Box>
                  </Box>
                  {/* Inline map panel */}
                  <Collapse in={isMapOpen}>
                    <Box
                      style={{
                        padding: 16,
                        backgroundColor: theme.palette.background.default,
                        borderTop: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {mapFetching[env.id] ? (
                        <Box display="flex" justifyContent="center" py={2}>
                          <CircularProgress size={28} />
                        </Box>
                      ) : (
                        <ResourceMapDiagram
                          workloadId={workloadId}
                          resources={mapData[env.id] ?? []}
                          onResourceClick={setDetailResource}
                        />
                      )}
                    </Box>
                  </Collapse>
                </Box>
                {/* Promote strip between environments (not after prod) */}
                {showPromote && nextEnv && (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    py={0.75}
                    style={{
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      backgroundColor: theme.palette.background.default,
                      gap: 8,
                    }}
                  >
                    <Typography variant="caption" style={{ opacity: 0.5, marginRight: 4 }}>
                      {env.display_name} → {nextEnv.display_name}:
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={
                        promoteLoading === `${env.id}->${nextEnv.id}`
                          ? <CircularProgress size={12} />
                          : <ArrowForwardIcon style={{ fontSize: 14 }} />
                      }
                      onClick={() => { setPromoteResult(null); handlePromote(env, nextEnv); }}
                      disabled={Boolean(promoteLoading) || !giteaSlug}
                      style={{ textTransform: 'none', fontSize: '0.72rem', padding: '2px 8px' }}
                    >
                      Open PR
                    </Button>
                  </Box>
                )}
                </React.Fragment>
              );
            })}
          </Box>
        )}

        {/* Inline result feedback */}
        {(scaleResult || rollbackResult || promoteResult) && (
          <>
            <Divider style={{ margin: '12px 0' }} />
            {scaleResult && (
              <Typography className={scaleResult.ok ? classes.resultText : classes.errorText}>
                Scale: {scaleResult.message}
              </Typography>
            )}
            {rollbackResult && (
              <Typography className={rollbackResult.ok ? classes.resultText : classes.errorText}>
                Rollback: {rollbackResult.message}
              </Typography>
            )}
            {promoteResult && (
              <Typography className={promoteResult.ok ? classes.resultText : classes.errorText}>
                Promote: {promoteResult.message}
                {promoteResult.ok && promoteResult.prUrl && (
                  <>
                    {' — '}
                    <a href={promoteResult.prUrl} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>
                      View PR
                    </a>
                  </>
                )}
              </Typography>
            )}
          </>
        )}
      </InfoCard>

      {/* ── Scale dialog ──────────────────────────────────────────────── */}
      <Dialog open={Boolean(scaleEnv)} onClose={() => !scaleLoading && setScaleEnv(null)}>
        <DialogTitle>Scale · {scaleEnv?.display_name}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Set replica count for <strong>{moduleName}</strong> in{' '}
            <strong>{scaleEnv?.display_name}</strong>.
          </DialogContentText>
          <Box className={classes.sliderRow}>
            <TuneIcon color="action" fontSize="small" />
            <Slider
              className={classes.slider}
              min={1} max={10} step={1}
              value={scaleValue}
              onChange={(_e, v) => setScaleValue(v as number)}
              marks valueLabelDisplay="on"
            />
            <Typography className={classes.replicaBadge}>{scaleValue}</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScaleEnv(null)} disabled={scaleLoading}>Cancel</Button>
          <Button
            color="primary" variant="contained"
            onClick={async () => { await handleScale(); if (scaleResult?.ok) setScaleEnv(null); }}
            disabled={scaleLoading}
            startIcon={scaleLoading ? <CircularProgress size={16} /> : undefined}
          >
            {scaleLoading ? 'Scaling…' : `Scale to ${scaleValue}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Rollback dialog ───────────────────────────────────────────── */}
      <Dialog open={Boolean(rollbackEnv)} onClose={() => !rollbackLoading && setRollbackEnv(null)} fullWidth maxWidth="sm">
        <DialogTitle>Rollback · {rollbackEnv?.display_name}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Select a previous deployment for <strong>{projectId}</strong> in{' '}
            <strong>{rollbackEnv?.display_name}</strong> to roll back to.
          </DialogContentText>
          {pastDeploysLoading && (
            <Box mt={2} display="flex" justifyContent="center"><CircularProgress size={24} /></Box>
          )}
          {!pastDeploysLoading && pastDeploys.length > 0 && (
            <Box mt={2}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Deployment</InputLabel>
                <Select
                  value={selectedDeploy}
                  onChange={e => setSelectedDeploy(e.target.value as string)}
                  label="Deployment"
                >
                  {pastDeploys.map(d => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.id} — {d.status} ({new Date(d.created_at).toLocaleString()})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          {!pastDeploysLoading && pastDeploys.length === 0 && (
            <Typography color="textSecondary" style={{ marginTop: 12 }}>No previous deployments found.</Typography>
          )}
          {rollbackResult && !rollbackResult.ok && (
            <Typography className={classes.errorText}>{rollbackResult.message}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRollbackEnv(null)} disabled={rollbackLoading}>Cancel</Button>
          <Button
            color="secondary" variant="contained"
            onClick={handleRollback}
            disabled={!selectedDeploy || rollbackLoading}
            startIcon={rollbackLoading ? <CircularProgress size={16} /> : undefined}
          >
            {rollbackLoading ? 'Rolling back…' : 'Confirm Rollback'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Resource detail dialog ───────────────────────────────────── */}
      {(() => {
        if (!detailResource) return null;
        const dr      = detailResource;
        const rawId   = dr.res_id ?? dr.resource_id ?? '';
        const label   = rawId.split('.').pop() ?? rawId;
        const rType   = dr.type ?? dr.resource_type ?? '';
        const rStat   = (dr.status || dr.deploy_status || dr.resource_status || dr.state || 'active') as string;

        // Known structured fields — shown in a clean table
        const knownFields: Array<{ key: string; label: string; value: unknown }> = [
          { key: 'res_id',         label: 'Resource ID',     value: dr.res_id },
          { key: 'gu_res_id',      label: 'Global Res ID',   value: dr.gu_res_id },
          { key: 'type',           label: 'Type',            value: dr.type },
          { key: 'status',         label: 'Status',          value: rStat },
          { key: 'driver_type',    label: 'Driver Type',     value: dr.driver_type },
          { key: 'driver_account', label: 'Driver Account',  value: dr.driver_account },
        ].filter(f => f.value !== undefined && f.value !== null && f.value !== '');

        // Extra fields from the catch-all index signature
        const knownKeys = new Set(['res_id', 'gu_res_id', 'type', 'status', 'deploy_status',
          'resource_status', 'state', 'driver_type', 'driver_account', 'resource_id', 'resource_type']);
        const extraFields = Object.entries(dr)
          .filter(([k]) => !knownKeys.has(k))
          .filter(([, v]) => v !== undefined && v !== null && v !== '');

        return (
          <Dialog
            open
            onClose={() => setDetailResource(null)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <Typography variant="h6" style={{ flex: 1 }}>{label}</Typography>
                <Chip size="small" label={rType} variant="outlined" />
                <Chip
                  size="small"
                  label={rStat}
                  style={{
                    backgroundColor:
                      rStat === 'active'  ? '#4caf5022' :
                      rStat === 'pending' ? '#ff980022' :
                      rStat === 'failed'  ? '#f4433622' : '#9e9e9e22',
                    color:
                      rStat === 'active'  ? '#4caf50' :
                      rStat === 'pending' ? '#ff9800' :
                      rStat === 'failed'  ? '#f44336' : '#9e9e9e',
                    borderColor: 'currentColor',
                    border: '1px solid',
                  }}
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box
                style={{
                  display: 'grid',
                  gridTemplateColumns: '140px 1fr',
                  gap: '6px 16px',
                  fontSize: '0.8rem',
                }}
              >
                {knownFields.map(({ key, label: lbl, value }) => (
                  <>
                    <Typography
                      key={`lbl-${key}`}
                      variant="caption"
                      style={{ color: theme.palette.text.secondary, alignSelf: 'center', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}
                    >
                      {lbl}
                    </Typography>
                    <Tooltip key={`val-${key}`} title={String(value)} placement="top-start">
                      <Typography
                        variant="body2"
                        style={{ fontFamily: 'monospace', wordBreak: 'break-all', cursor: 'default', alignSelf: 'center' }}
                      >
                        {String(value)}
                      </Typography>
                    </Tooltip>
                  </>
                ))}
              </Box>

              {extraFields.length > 0 && (
                <>
                  <Divider style={{ margin: '16px 0 12px' }} />
                  <Typography variant="caption" style={{ opacity: 0.6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Additional fields
                  </Typography>
                  <Box
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '140px 1fr',
                      gap: '6px 16px',
                      marginTop: 8,
                      fontSize: '0.8rem',
                    }}
                  >
                    {extraFields.map(([k, v]) => (
                      <>
                        <Typography
                          key={`xlbl-${k}`}
                          variant="caption"
                          style={{ color: theme.palette.text.secondary, alignSelf: 'center', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}
                        >
                          {k}
                        </Typography>
                        <Tooltip key={`xval-${k}`} title={typeof v === 'object' ? JSON.stringify(v) : String(v)} placement="top-start">
                          <Typography
                            variant="body2"
                            style={{ fontFamily: 'monospace', wordBreak: 'break-all', cursor: 'default', alignSelf: 'center' }}
                          >
                            {typeof v === 'object' ? JSON.stringify(v, null, 2) : String(v)}
                          </Typography>
                        </Tooltip>
                      </>
                    ))}
                  </Box>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailResource(null)}>Close</Button>
            </DialogActions>
          </Dialog>
        );
      })()}

    </>
  );
};
